import { Avatar, DS } from "../ds/Kit";
import { FC, useEffect, useState, useMemo } from "react";
import { GD } from "src/GD";
import Button from "@ui/button/Button";
import { SignalHandler } from "badmfck-signal";
import { Box, HBox, VBox } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import { StatusBadge } from "./StatusBadge";
import { toast } from "react-toastify";
import { Error as ErrorNotice } from "@ui/error/Error";
import { SpinnerProgress } from "@ui/spinner/SpinnerProgress";
import { useWindowSize } from "@ui/utils/useResize";

const INVITE_LIST_PANEL_BORDER = "#E2E8F0";
const INVITE_LIST_BRAND = "#27445D";
const INVITE_LIST_TEXT = "#1F2937";
const INVITE_LIST_MUTED = "#4B5563";
const INVITE_LIST_SOFT = "#F8FAFC";

const formatInviteDate = (value?: string | Date | null) => {
	if (!value) return "N/A";

	return new Date(value).toLocaleDateString("en-GB", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
};

const formatInviteTime = (value?: string | Date | null) => {
	if (!value) return "";

	return new Date(value).toLocaleTimeString("en-GB", {
		hour: "2-digit",
		minute: "2-digit",
	});
};

const RESENDABLE_STATUSES = ["sent", "opened", "error"];
// Team gone or no longer led by the inviter: re-invite through the dialog with a fresh team choice
const NEEDS_TEAM_CHOICE = ["Teamlead can invite only into teams they lead", "Team does not belong to this company", "team_uid is required when inviting a buyer"];

export const getInviteDisplayStatus = (invite: IInvite, now = Date.now()) =>
	(invite.status === "sent" || invite.status === "opened") && invite.expires_at && new Date(invite.expires_at).getTime() < now ? "expired" : invite.status;

export const InvitesList: FC<{ onReinvite?: (invite: IInvite) => void }> = ({ onReinvite }) => {
	const { width: viewportWidth } = useWindowSize();
	const isMobile = viewportWidth <= 768;
	const [invites, , setInvites, busy] = GD.REQ_INVITES_LIST.useRequest();
	const [filter, setFilter] = useState<{
		name: string;
		email: string;
		role: string;
		status: string;
	}>({
		name: "",
		email: "",
		role: "all",
		status: "all",
	});
	const [resending, setResending] = useState<number[]>([]);

	useEffect(() => {
		const sh = new SignalHandler();
		sh.add(GD.S_INVITES_LIST_UPDATED, data => {
			setInvites({ data });
		});
		return () => {
			sh.clear();
		};
	}, []);

	const filteredData = useMemo(() => {
		if (!invites?.data) return [];
		return invites.data.filter(invite => {
			if (filter.name && !invite.name?.toLowerCase().includes(filter.name.toLowerCase())) return false;
			if (filter.email && !invite.email.toLowerCase().includes(filter.email.toLowerCase())) return false;
			if (filter.role !== "all" && invite.role !== filter.role) return false;

			return filter.status === "all" || getInviteDisplayStatus(invite) === filter.status;
		});
	}, [invites, filter]);

	const getSelectValue = (value: string | { value?: string }) => (typeof value === "string" ? value : value.value ?? "all");

	const onResend = async (invite: IInvite) => {
		if (!confirm(`Send the invite to ${invite.email} again? The previous link will stop working.`)) return;
		setResending(ids => [...ids, invite.id]);
		try {
			const res = await GD.REQ_INVITE_USER.request({ name: invite.name, email: invite.email, role: invite.role, team_uid: invite.team_uid });
			if (!res.error) {
				toast.success("Invite sent again");
				return;
			}
			const text = res.error.details || res.error.message;
			if (onReinvite && NEEDS_TEAM_CHOICE.some(m => text?.includes(m))) {
				toast.info("Choose a team for this invite.");
				onReinvite(invite);
			} else toast.error(text);
		} finally {
			setResending(ids => ids.filter(id => id !== invite.id));
		}
	};

	const onDelete = async (invite: IInvite) => {
		if (!confirm(`Delete invite for ${invite.email}?`)) return;

		if (invite.id) {
			const res = await GD.REQ_INVITE_DELETE.request({ id: String(invite.id) });
			if (!res.error) {
				toast.success("Invite deleted");
			}
		} else {
			console.warn("Invite has no ID", invite);
		}
	};

	return (
		<VBox
			sx={{
				height: isMobile ? "auto" : "100%",
				minHeight: isMobile ? "auto" : 300,
				overflow: isMobile ? "visible" : "hidden",
			}}
		>
			<Box
				sx={{
					padding: isMobile ? "12px" : "16px 24px",
					backgroundColor: INVITE_LIST_SOFT,
					borderBottom: `1px solid ${INVITE_LIST_PANEL_BORDER}`,
				}}>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "minmax(150px, 220px) minmax(180px, 260px) 150px 150px",
						gap: "12px",
						alignItems: "end",
						"@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px" },
					}}
				>
					<Input
						label="Name"
						size="small"
						data={filter}
						name="name"
						onChange={v => setFilter(p => ({ ...p, name: v }))}
						sx={{ width: "100%", minWidth: 0, "@media (max-width: 600px)": { gridColumn: "1 / -1" } }}
					/>
					<Input
						label="Email"
						size="small"
						data={filter}
						name="email"
						onChange={v => setFilter(p => ({ ...p, email: v }))}
						sx={{ width: "100%", minWidth: 0, "@media (max-width: 600px)": { gridColumn: "1 / -1" } }}
					/>
					<Box sx={{ minWidth: 0 }}>
						<Select
							label="Role"
							size="small"
							data={filter}
							name="role"
							values={[
								{ name: "All", value: "all" },
								{ name: "Admin", value: "admin" },
								{ name: "Team Lead", value: "teamlead" },
								{ name: "Buyer", value: "buyer" },
							]}
							onChange={v => setFilter(p => ({ ...p, role: getSelectValue(v) }))}
							sx={{ width: "100%" }}
						/>
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<Select
							label="Status"
							size="small"
							data={filter}
							name="status"
							values={[
								{ name: "All", value: "all" },
								{ name: "Sent", value: "sent" },
								{ name: "Opened", value: "opened" },
								{ name: "Used", value: "used" },
								{ name: "Error", value: "error" },
								{ name: "Expired", value: "expired" },
							]}
							onChange={v => setFilter(p => ({ ...p, status: getSelectValue(v) }))}
							sx={{ width: "100%" }}
						/>
					</Box>
				</Box>
			</Box>
			<Box
				sx={{
					flex: isMobile ? "none" : 1,
					overflowY: isMobile ? "visible" : "auto",
					overflowX: "hidden",
					padding: isMobile ? "10px" : "12px",
					backgroundColor: "#fff",
				}}
			>
				<SpinnerProgress busy={Boolean(busy)} />
				{invites?.error && <ErrorNotice error={invites.error} sx={{ margin: "12px" }} />}
				{!busy && !invites?.error && isMobile && filteredData.length === 0 && (
					<VBox center gap="5px" sx={{ padding: "40px 20px" }}>
						<Typo sx={{ color: INVITE_LIST_TEXT, fontWeight: 700 }}>No invites found</Typo>
						<Typo sx={{ color: INVITE_LIST_MUTED, fontSize: "12px" }}>Try changing the filters.</Typo>
					</VBox>
				)}
				{!busy && !invites?.error && isMobile && (
					<VBox gap="10px">
						{filteredData.map(row => {
							const initials = row.name ? row.name.substring(0, 2).toUpperCase() : "I";
							return (
								<Box
									key={row.id}
									sx={{ padding: "13px", border: `1px solid ${INVITE_LIST_PANEL_BORDER}`, borderRadius: "12px", backgroundColor: "#FFFFFF", boxShadow: "0 3px 12px rgba(15, 23, 42, 0.035)" }}
								>
									<HBox sx={{ alignItems: "flex-start", gap: "11px", minWidth: 0 }}>
										<Box sx={{ width: "40px", height: "40px", borderRadius: "10px", flexShrink: 0, display: "grid", placeItems: "center", backgroundColor: "#F1F5F9", color: INVITE_LIST_BRAND, fontWeight: 800, fontSize: "12px" }}>
											{initials}
										</Box>
										<VBox gap="3px" sx={{ minWidth: 0, flex: 1 }}>
											<Typo sx={{ color: INVITE_LIST_TEXT, fontSize: "14px", fontWeight: 750, lineHeight: 1.2, overflowWrap: "anywhere" }}>{row.name || "N/A"}</Typo>
											<Typo sx={{ color: INVITE_LIST_MUTED, fontSize: "11px", lineHeight: 1.3, overflowWrap: "anywhere", userSelect: "text" }}>{row.email || "N/A"}</Typo>
										</VBox>
									</HBox>
									<HBox gap="7px" sx={{ marginTop: "10px", alignItems: "center", flexWrap: "wrap" }}>
										<StatusBadge status={row.role} type="role" />
										<StatusBadge status={getInviteDisplayStatus(row)} />
									</HBox>
									<HBox sx={{ marginTop: "11px", paddingTop: "10px", borderTop: `1px solid ${INVITE_LIST_PANEL_BORDER}`, alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
										<VBox gap="2px">
											<Typo sx={{ color: "#94A3B8", fontSize: "10px" }}>Created</Typo>
											<Typo sx={{ color: INVITE_LIST_TEXT, fontSize: "11px", fontWeight: 650 }}>{formatInviteDate(row.created_at)} {formatInviteTime(row.created_at)}</Typo>
										</VBox>
										<HBox gap="8px" sx={{ flexShrink: 0 }}>
											{RESENDABLE_STATUSES.includes(row.status) && (
												<Button variant="secondary" disabled={resending.includes(row.id)} onClick={() => onResend(row)} sx={{ minHeight: "44px", padding: "7px 12px", borderRadius: "10px", fontSize: "12px" }}>Resend</Button>
											)}
											<Button variant="secondary" onClick={() => onDelete(row)} sx={{ flexShrink: 0, minHeight: "44px", padding: "7px 12px", borderRadius: "10px", color: "#A63452", borderColor: "#EBDDE2", fontSize: "12px" }}>Delete invite</Button>
										</HBox>
									</HBox>
								</Box>
							);
						})}
					</VBox>
				)}
				{!busy && !invites?.error && !isMobile && (
					<VBox sx={{ minWidth: 0 }}>
						<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1.8fr) 110px 100px 130px 84px", gap: "16px", padding: "10px 20px", background: DS.soft, borderBottom: `1px solid ${DS.line}`, position: "sticky", top: 0, zIndex: 1 }}>
							{["Invite", "Email", "Role", "Status", "Created", ""].map(label => (
								<Typo key={label || "actions"} sx={{ fontSize: "12px", fontWeight: 600, color: DS.muted }}>{label}</Typo>
							))}
						</Box>
						{filteredData.length === 0 ? (
							<VBox gap="6px" sx={{ padding: "48px 24px", alignItems: "center", textAlign: "center" }}>
								<Typo sx={{ color: DS.ink, fontWeight: 650 }}>No invites found</Typo>
								<Typo sx={{ color: DS.muted, fontSize: "13px" }}>Invite someone with the Invite user button, or change the filters.</Typo>
							</VBox>
						) : filteredData.map(row => (
							<Box
								key={row.id ?? row.email}
								sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1.8fr) 110px 100px 130px 84px", gap: "16px", alignItems: "center", padding: "12px 20px", borderBottom: `1px solid ${DS.line}`, "&:hover": { background: "#FAFBFF" } }}
							>
								<HBox gap="12px" sx={{ alignItems: "center", minWidth: 0 }}>
									<Avatar name={row.name || row.email} size={34} />
									<VBox gap="2px" sx={{ minWidth: 0 }}>
										<Typo sx={{ fontSize: "14px", fontWeight: 650, color: DS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.name || "Unnamed invite"}</Typo>
										<Typo sx={{ fontSize: "12px", color: DS.faint }}>{`ID ${row.id ?? "—"}`}</Typo>
									</VBox>
								</HBox>
								<Typo sx={{ fontSize: "13px", color: DS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.email || "—"}</Typo>
								<Box><StatusBadge status={row.role} type="role" /></Box>
								<Box><StatusBadge status={getInviteDisplayStatus(row)} /></Box>
								<VBox gap="1px">
									<Typo sx={{ fontSize: "13px", color: DS.ink }}>{formatInviteDate(row.created_at)}</Typo>
									{row.created_at && <Typo sx={{ fontSize: "12px", color: DS.faint }}>{formatInviteTime(row.created_at)}</Typo>}
								</VBox>
								<HBox sx={{ alignItems: "center", justifyContent: "flex-end" }}>
								{RESENDABLE_STATUSES.includes(row.status) && (
									<Box
										element="button"
										type="button"
										aria-label="Resend invite"
										title="Resend invite"
										disabled={resending.includes(row.id)}
										onClick={() => onResend(row)}
										sx={{ width: "34px", height: "34px", border: 0, borderRadius: "10px", background: "transparent", color: DS.faint, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { background: DS.soft, color: DS.ink }, "&:disabled": { opacity: 0.4, cursor: "not-allowed" } }}
									>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></svg>
									</Box>
								)}
								<Box
									element="button"
									type="button"
									aria-label="Delete invite"
									onClick={() => onDelete(row)}
									sx={{ width: "34px", height: "34px", border: 0, borderRadius: "10px", background: "transparent", color: DS.faint, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { background: "#FEF2F2", color: "#B91C1C" } }}
								>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /></svg>
								</Box>
								</HBox>
							</Box>
						))}
					</VBox>
				)}
			</Box>
		</VBox>
	);
};
